import {
  Box,
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuProps,
  Stack,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import * as React from 'react';
import {
  Control,
  Controller,
  ControllerFieldState,
  ControllerRenderProps,
  FieldValues,
  Path,
  RegisterOptions,
  UseFormStateReturn,
} from 'react-hook-form';

import { UserPlus02Icon } from '@/components/icons/UserPlus02Icon';
import {
  getFieldErrorValue,
  getValidations,
} from '@/components/utils/inputUtils';
import { useFormSideDrawerMenuProps } from '@/modules/common/SideDrawer/FormSideDrawer';
import { useSelectInputContext } from '@/modules/entities/contexts/selectInput/selectInput.context';
import { FieldNameFromFormShape } from '@/types/react-hook-form';

import {
  AddNewOptionDefinition,
  HelpTextVariant,
  SelectInputOption,
} from '../baseInputs/inputTypes';
import { SelectInput } from '../baseInputs/SelectInput';
import { useFormFieldsDisabled } from '../context/formFieldsDisabled.context';
import { useResponsiveSelectInput } from './hooks/useResponsiveSelectInput';

export type InputOption =
  | SelectInputOption
  | { type: 'component'; component: JSX.Element };

export interface FormAwareSelectInputProps<
  FormShape extends FieldValues,
  V = unknown,
> {
  fieldName: FieldNameFromFormShape<FormShape>;
  label: string;
  hideLabel?: boolean;
  helpText?: string;
  helpTextVariant?: HelpTextVariant;
  options: InputOption[];
  addNewOption?: AddNewOptionDefinition;
  required?: boolean;
  control: Control<FormShape>;
  disabled?: boolean;
  validation?: RegisterOptions<FormShape>['validate'];
  contextualHelp?: JSX.Element;
  startAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode;
  testId?: string;
  id?: string;
  disableAutomaticNewOptionSelection?: boolean;
  labelIconEnd?: React.ReactNode;
  rightLocatorIcon?: React.ReactNode;
  emptyValueDisplay?: string;
  showEmptyValue?: boolean;
  renderValue?: (value: V) => React.ReactNode;
  loading?: boolean;
  MenuProps?: Partial<MenuProps>;
  showErrorBeforeSubmit?: boolean;
}

export type ResponsiveSelectInputProps<FormShape extends FieldValues> = {
  field: ControllerRenderProps<FormShape, Path<FormShape>>;
  fieldState: ControllerFieldState;
  formState: UseFormStateReturn<FormShape>;
} & FormAwareSelectInputProps<FormShape>;

function ResponsiveSelectInput<FormShape extends FieldValues>({
  field,
  fieldState,
  formState,
  ...selectInputProps
}: ResponsiveSelectInputProps<FormShape>) {
  const { disabled: disabledFromContext } = useFormFieldsDisabled();
  const {
    removeSelectFromStack,
    pushActiveSelectInput,
    peekActiveSelectInput,
    popActiveSelectInput,
  } = useSelectInputContext();
  // When inside a FormSideDrawer, we need to disable portal rendering so the
  // dropdown menu stays within the drawer's stacking context and avoids z-index conflicts.
  const sideDrawerMenuProps = useFormSideDrawerMenuProps();

  // Control the open state of the select input
  const [isOpen, setisOpen] = useState(false);

  const {
    label,
    helpText,
    helpTextVariant,
    options,
    addNewOption,
    hideLabel,
    required,
    disabled,
    contextualHelp,
    startAdornment,
    endAdornment,
    testId,
    id,
    fieldName,
    labelIconEnd,
    emptyValueDisplay,
    showEmptyValue,
    renderValue,
    loading,
    MenuProps: menuPropsFromProps,
    showErrorBeforeSubmit,
  } = selectInputProps;

  useEffect(() => {
    return () => {
      // When the component unmounts, we want to remove it from the activeSelectStack.
      removeSelectFromStack(fieldName as string);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClose = useCallback(() => {
    const activeSelectInput = peekActiveSelectInput();

    if (activeSelectInput === fieldName) {
      // If we are closing the active select input, we want to pop it off the stack.
      popActiveSelectInput();
    }

    setisOpen(false);
  }, [fieldName, peekActiveSelectInput, popActiveSelectInput]);

  const handleOpen = useCallback(() => {
    pushActiveSelectInput(fieldName as string);
    setisOpen(true);
  }, [fieldName, pushActiveSelectInput]);

  useResponsiveSelectInput<FormShape>({
    fieldName,
    fieldValue: field.value,
    selectValue: field.onChange,
    options,
    disableAutomaticNewOptionSelection:
      selectInputProps.disableAutomaticNewOptionSelection,
    handleClose,
    isOpen,
  });

  const finalOptions = useMemo(() => {
    if (!addNewOption) return options;
    return [
      {
        type: 'component' as const,
        component: (
          // Close the open menu when the add-new action is chosen. This item is a
          // wrapped custom component, so MUI doesn't treat it as a selectable
          // MenuItem and won't fire the Select's onClose for us. We only close the
          // visual menu (setisOpen) rather than calling handleClose: popping this
          // field off the active-select stack would start the auto-select grace
          // period (useResponsiveSelectInput), and creation modals routinely take
          // longer than that window, which would break auto-selecting the new
          // option for callers that rely on it.
          <MenuItem
            onClick={() => {
              setisOpen(false);
              addNewOption.onClick();
            }}
          >
            <ListItemIcon>
              {addNewOption.icon || <UserPlus02Icon size={16} />}
            </ListItemIcon>
            <ListItemText>{addNewOption.text || 'Add new'}</ListItemText>
          </MenuItem>
        ),
      },
      ...options,
    ];
  }, [addNewOption, options]);

  return (
    <SelectInput
      hideLabel={hideLabel}
      testId={testId}
      id={id}
      label={label}
      options={finalOptions}
      onChange={field.onChange}
      onBlur={field.onBlur}
      value={field.value}
      name={field.name}
      inputRef={field.ref}
      required={required}
      helpText={helpText}
      helpTextVariant={helpTextVariant}
      disabled={disabledFromContext ?? disabled}
      contextualHelp={contextualHelp}
      errorMessage={getFieldErrorValue(
        fieldState,
        formState.isSubmitted,
        showErrorBeforeSubmit
      )}
      startAdornment={startAdornment}
      endAdornment={endAdornment}
      open={isOpen}
      onClose={handleClose}
      onOpen={handleOpen}
      labelIconEnd={labelIconEnd}
      emptyValueDisplay={emptyValueDisplay}
      showEmptyValue={showEmptyValue}
      renderValue={renderValue}
      loading={loading}
      MenuProps={{ ...sideDrawerMenuProps, ...menuPropsFromProps }}
    />
  );
}

export function FormAwareSelectInput<FormShape extends FieldValues>(
  props: FormAwareSelectInputProps<FormShape>
) {
  const { fieldName, label, required, control, validation } = props;

  const validations = getValidations(label, !!required, validation);

  return (
    <Controller
      name={fieldName}
      control={control}
      rules={{ validate: validations }}
      render={({ field, fieldState, formState }) => {
        const selectInput = (
          <ResponsiveSelectInput
            field={field}
            fieldState={fieldState}
            formState={formState}
            {...props}
          />
        );

        if (!props?.rightLocatorIcon) {
          // Render the select input, as is.
          return selectInput;
        }

        // Render the select input, with the right locator icon.
        return (
          <Stack direction="row" spacing={1} alignItems="center">
            <Box sx={{ flexGrow: 1 }}>{selectInput}</Box>
            {props?.rightLocatorIcon}
          </Stack>
        );
      }}
    />
  );
}
